import { useState } from 'react'
import { toast } from 'sonner'
import inboxApi from '@/state/inbox/inbox.slice'
import type { InboxAssetType } from '@/state/entities/inbox/inbox.types'

const MAX_SIZE_BYTES = 104857600 // 100 MB

interface UploadOptions {
  folder?: string | null
  serial?: string | null
  threadId?: string | null
}

/**
 * Orchestrates the direct-to-S3 upload dance:
 *   1. initiate  -> server returns a presigned PUT url + headers
 *   2. PUT       -> browser uploads the file straight to S3
 *   3. confirm   -> server verifies it landed and marks it live
 */
export function useAssetUpload(orgId: string) {
  const [initiate] = inboxApi.useInitiateAssetUploadMutation()
  const [confirm] = inboxApi.useConfirmAssetUploadMutation()
  const [isUploading, setIsUploading] = useState(false)

  const uploadAsset = async (
    file: File,
    options?: UploadOptions
  ): Promise<InboxAssetType | null> => {
    if (file.size > MAX_SIZE_BYTES) {
      toast.error('That file is too large (max 100MB).')
      return null
    }

    setIsUploading(true)
    try {
      const init = await initiate({
        orgId,
        data: {
          filename: file.name,
          mime_type: file.type,
          size: file.size,
          folder: options?.folder,
          serial: options?.serial,
          thread_id: options?.threadId,
        },
      }).unwrap()

      const put = await fetch(init.upload_url, {
        method: 'PUT',
        headers: init.headers,
        body: file,
      })
      if (!put.ok) {
        throw new Error(`S3 upload failed with status ${put.status}`)
      }

      const { asset } = await confirm({
        orgId,
        assetId: init.asset_id,
      }).unwrap()

      return asset
    } catch (error) {
      toast.error('Upload failed. Please try again.')
      console.error('Asset upload failed', error)
      return null
    } finally {
      setIsUploading(false)
    }
  }

  return { uploadAsset, isUploading }
}
